import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { expVal } from '@confluence/feature-experiments';
import {
	PageTitleQuery,
	type PageTitleQuery$data,
	type PageTitleQuery$variables,
} from '@confluence/content-title/entry-points/PageTitleQuery.graphql.ts';

import FolderOverviewFirstPageQueryNode, {
	type FolderOverviewFirstPageQuery$data,
	type FolderOverviewFirstPageQuery$variables,
} from './__generated__/FolderOverviewFirstPageQuery.graphql.ts';
import FolderOverviewChildrenContentQueryNode, {
	type FolderOverviewChildrenContentQuery$data,
	type FolderOverviewChildrenContentQuery$variables,
} from './__generated__/FolderOverviewChildrenContentQuery.graphql.ts';
import FolderRedirectFirstChildQueryNode, {
	type FolderRedirectFirstChildQuery$data,
	type FolderRedirectFirstChildQuery$variables,
} from './FolderLanding/__generated__/FolderRedirectFirstChildQuery.graphql.ts';
import DualStreamQueriesFirstPageQueryNode, {
	type DualStreamQueriesFirstPageQuery$data,
	type DualStreamQueriesFirstPageQuery$variables,
} from './__generated__/DualStreamQueriesFirstPageQuery.graphql.ts';
import { FETCH_SIZE, getInitialVisibleChildIds } from './dualStreamVisibleNodes.ts';

type PreloadFolderDataOptions = {
	/** Cloud ID — required by the dual-stream content query. */
	cloudId?: string;
	/** Space key — required by the dual-stream content query. */
	spaceKey?: string;
};

/** The `{ data, errors, hasErrors }` wrapper that `query()` resolves to. */
type QueryResult<TData> = {
	data: TData | null;
	hasErrors: boolean;
};

/**
 * Warms `FolderOverviewChildrenContentQuery` (child history + collaborators) once
 * the overview result is known.
 *
 * This is a chained preload: the enrichment query's `ids` depend on the child ids
 * returned by the first-page query. It runs last so it does not block the
 * independent preloads. The `ids` must match the component's first-render request
 * exactly (order included) or the SSR-warmed store snapshot is treated as
 * incomplete and the columns render blank — see `FolderOverview.tsx`.
 */
async function preloadChildrenContent<TData>(
	overviewTask: Promise<QueryResult<TData>>,
	extractChildIds: (data: TData) => string[],
): Promise<void | QueryResult<FolderOverviewChildrenContentQuery$data>> {
	const { data, hasErrors } = await overviewTask;
	if (hasErrors || !data) {
		return;
	}
	const childIds = extractChildIds(data);
	if (childIds.length === 0) {
		return;
	}
	return query<
		FolderOverviewChildrenContentQuery$data,
		FolderOverviewChildrenContentQuery$variables
	>({
		query: FolderOverviewChildrenContentQueryNode,
		variables: { ids: childIds },
	});
}

/** Child ids from the legacy first-page query, in `ptpage.children.nodes` order. */
const legacyChildIds = (data: FolderOverviewFirstPageQuery$data): string[] => {
	const ids: string[] = [];
	for (const node of data.ptpage?.children?.nodes ?? []) {
		if (node?.id) {
			ids.push(node.id);
		}
	}
	return ids;
};

export async function preloadFolderData(
	contentId: string,
	{ cloudId, spaceKey }: PreloadFolderDataOptions = {},
): Promise<void | Error[]> {
	// Redirect + title preloads are independent of the overview result — start
	// them immediately so they run concurrently with the overview query.
	//
	// Always preload the redirect query: the folder landing route
	// (`confluence_folder_view` OFF) renders `FolderRedirect`, which consumes it.
	const redirectQueryTask = query<
		FolderRedirectFirstChildQuery$data,
		FolderRedirectFirstChildQuery$variables
	>({
		query: FolderRedirectFirstChildQueryNode,
		variables: { id: contentId },
	});
	// Preload the folder header title. `FolderHeader` → `FolderInlineTitle`
	// consumes it via `usePageTitle`, which only fetches on the client; without
	// this the folder title renders empty during SSR.
	const titleQueryTask = query<PageTitleQuery$data, PageTitleQuery$variables>({
		query: PageTitleQuery,
		variables: { contentId },
	});

	let overviewQueryTask: Promise<QueryResult<unknown>>;
	let childrenQueryTask: Promise<void | QueryResult<FolderOverviewChildrenContentQuery$data>>;

	// The render path branches on `cc_folder_view_type_filtered_children`
	// (see FolderOverview.tsx): the dual-stream path fires
	// `DualStreamQueriesFirstPageQuery` while the legacy path fires
	// `FolderOverviewFirstPageQuery`. Preload whichever one the render will
	// actually consume so its data is in the store before the first SSR render,
	// then chain the children-content preload off its result.
	if (expVal('cc_folder_view_type_filtered_children', 'isEnabled', false)) {
		const dualStreamTask = query<
			DualStreamQueriesFirstPageQuery$data,
			DualStreamQueriesFirstPageQuery$variables
		>({
			query: DualStreamQueriesFirstPageQueryNode,
			// These variables MUST match the ones passed by `useDualStreamPagination`
			// (empty-string fallbacks for cloudId/spaceKey included) so the store
			// snapshot is complete for the component's operation on SSR.
			variables: {
				cloudId: cloudId ?? '',
				parentId: contentId,
				spaceKey: spaceKey ?? '',
				contentId,
				first: FETCH_SIZE,
				includeDevDocsSyncStatus: expVal('confluence_dev_docs', 'isEnabled', false),
			},
		});
		overviewQueryTask = dualStreamTask;
		childrenQueryTask = preloadChildrenContent(dualStreamTask, (data) =>
			getInitialVisibleChildIds(data.folders, data.content),
		);
	} else {
		const legacyTask = query<
			FolderOverviewFirstPageQuery$data,
			FolderOverviewFirstPageQuery$variables
		>({
			query: FolderOverviewFirstPageQueryNode,
			variables: {
				id: contentId,
				after: null,
				contentId,
				includeDevDocsSyncStatus: expVal('confluence_dev_docs', 'isEnabled', false),
			},
		});
		overviewQueryTask = legacyTask;
		childrenQueryTask = preloadChildrenContent(legacyTask, legacyChildIds);
	}

	// `waitForAllReturnWhenError` submits any task errors for observability and
	// resolves to `Error[]` on failure or `undefined` on success — its
	// `Awaited<T>[]` return arm is never produced at runtime, so narrow to the
	// real success/error contract.
	const errors = await waitForAllReturnWhenError([
		overviewQueryTask,
		redirectQueryTask,
		titleQueryTask,
		childrenQueryTask,
	]);
	return Array.isArray(errors) ? (errors as Error[]) : undefined;
}
